import { IAttachment } from '@app/models/Attachment'
import {
  DevelopmentClient,
  IDevelopmentClient,
} from '@app/models/DevelopmentClient'
import {
  EntityPermissions,
  IEntityPermissions,
} from '@app/models/EntityPermissions'
import { ApplicationState } from '@app/store'
import { errorToast } from '@app/utils/notifications'
import { makeAuthenticatedRequest, RequestMethod } from '@app/utils/request'
import { List, Record } from 'immutable'
import { push } from 'connected-react-router'
import { Action, Reducer } from 'redux'
import { fork, put, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

// -------------------------
// STATE
// -------------------------

export enum DevelopmentClientViewScope {
  Photos = 'Photos',
  Attachments = 'Attachments',
}

export interface IDevelopmentClientViewState {
  readonly error: string
  readonly isDeleting: boolean
  readonly isLoading: boolean
  readonly item: IDevelopmentClient
  readonly permissions: IEntityPermissions
  readonly scope: DevelopmentClientViewScope
  readonly showConfirmDelete: boolean
}

const DevelopmentClientViewRecord = Record({
  error: '',
  isDeleting: false,
  isLoading: false,
  item: new DevelopmentClient(),
  permissions: new EntityPermissions(),
  scope: DevelopmentClientViewScope.Photos,
  showConfirmDelete: false,
})

export class DevelopmentClientViewState extends DevelopmentClientViewRecord
  implements IDevelopmentClientViewState {
  public static fromJS(
    initialDevelopmentClientView?: Partial<IDevelopmentClientViewState>,
  ) {
    return new DevelopmentClientViewState(initialDevelopmentClientView)
  }

  public readonly error: string
  public readonly isDeleting: boolean
  public readonly isLoading: boolean
  public readonly item: DevelopmentClient
  public readonly permissions: EntityPermissions
  public readonly scope: DevelopmentClientViewScope
  public readonly showConfirmDelete: boolean

  constructor(props?: Partial<IDevelopmentClientViewState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum ActionTypes {
  DeleteDevelopmentClient = 'DEVELOPMENT_CLIENT_VIEW::DELETE',
  DeleteDevelopmentClientFailure = 'DEVELOPMENT_CLIENT_VIEW::DELETE_FAILURE',
  DeleteDevelopmentClientRequest = 'DEVELOPMENT_CLIENT_VIEW::DELETE_REQUEST',
  DeleteDevelopmentClientSuccess = 'DEVELOPMENT_CLIENT_VIEW::DELETE_SUCCESS',
  LoadDevelopmentClient = 'DEVELOPMENT_CLIENT_VIEW::LOAD_DEVELOPMENT_CLIENT',
  LoadDevelopmentClientRequest = 'DEVELOPMENT_CLIENT_VIEW::LOAD_DEVELOPMENT_CLIENT_REQUEST',
  LoadDevelopmentClientSuccess = 'DEVELOPMENT_CLIENT_VIEW::LOAD_DEVELOPMENT_CLIENT_SUCCESS',
  LoadDevelopmentClientFailure = 'DEVELOPMENT_CLIENT_VIEW::LOAD_DEVELOPMENT_CLIENT_FAILURE',
  ResetDevelopmentClientView = 'DEVELOPMENT_CLIENT_VIEW::RESET_STATE',
  SetAttachments = 'DEVELOPMENT_CLIENT_VIEW::SET_ATTACHMENTS',
  SetScope = 'DEVELOPMENT_CLIENT_VIEW::SET_SCOPE',
  ToggleConfirmDelete = 'DEVELOPMENT_CLIENT_VIEW::TOGGLE_CONFIRM_DELETE',
}

interface IDeleteDevelopmentClientAction {
  id: string
  type: ActionTypes.DeleteDevelopmentClient
}

interface IDeleteDevelopmentClientFailureAction {
  error: string
  type: ActionTypes.DeleteDevelopmentClientFailure
}

interface IDeleteDevelopmentClientRequestAction {
  type: ActionTypes.DeleteDevelopmentClientRequest
}

interface IDeleteDevelopmentClientSuccessAction {
  type: ActionTypes.DeleteDevelopmentClientSuccess
}

interface ILoadDevelopmentClientFailureAction {
  error: string
  type: ActionTypes.LoadDevelopmentClientFailure
}

interface ILoadDevelopmentClientRequestAction {
  type: ActionTypes.LoadDevelopmentClientRequest
}

interface ILoadDevelopmentClientSuccessAction {
  item: DevelopmentClient
  permissions: EntityPermissions
  type: ActionTypes.LoadDevelopmentClientSuccess
}

interface ILoadDevelopmentClientViewAction {
  slug: string
  type: ActionTypes.LoadDevelopmentClient
}

interface IResetDevelopmentClientViewAction {
  type: ActionTypes.ResetDevelopmentClientView
}

interface ISetAttachmentsAction {
  type: ActionTypes.SetAttachments
  value: List<IAttachment>
}

interface ISetScopeAction {
  scope: DevelopmentClientViewScope
  type: ActionTypes.SetScope
}

interface IToggleConfirmDeleteAction {
  show: boolean
  type: ActionTypes.ToggleConfirmDelete
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | IDeleteDevelopmentClientFailureAction
  | IDeleteDevelopmentClientRequestAction
  | IDeleteDevelopmentClientSuccessAction
  | ILoadDevelopmentClientFailureAction
  | ILoadDevelopmentClientRequestAction
  | ILoadDevelopmentClientSuccessAction
  | IResetDevelopmentClientViewAction
  | ISetAttachmentsAction
  | ISetScopeAction
  | IToggleConfirmDeleteAction

// -------------------------
// ACTION CREATORS
// -------------------------
function deleteDevelopmentClient(id: string): IDeleteDevelopmentClientAction {
  return {
    id,
    type: ActionTypes.DeleteDevelopmentClient,
  }
}

function deleteDevelopmentClientFailure(
  error: string,
): IDeleteDevelopmentClientFailureAction {
  return {
    error,
    type: ActionTypes.DeleteDevelopmentClientFailure,
  }
}

function deleteDevelopmentClientRequest(): IDeleteDevelopmentClientRequestAction {
  return {
    type: ActionTypes.DeleteDevelopmentClientRequest,
  }
}

function deleteDevelopmentClientSuccess(): IDeleteDevelopmentClientSuccessAction {
  return {
    type: ActionTypes.DeleteDevelopmentClientSuccess,
  }
}

function loadDevelopmentClient(slug: string): ILoadDevelopmentClientViewAction {
  return { slug, type: ActionTypes.LoadDevelopmentClient }
}

function loadDevelopmentClientFailure(
  error: string,
): ILoadDevelopmentClientFailureAction {
  return { error, type: ActionTypes.LoadDevelopmentClientFailure }
}

function loadDevelopmentClientRequest(): ILoadDevelopmentClientRequestAction {
  return { type: ActionTypes.LoadDevelopmentClientRequest }
}

function loadDevelopmentClientSuccess(
  item: DevelopmentClient,
  permissions: EntityPermissions,
): ILoadDevelopmentClientSuccessAction {
  return {
    item,
    permissions,
    type: ActionTypes.LoadDevelopmentClientSuccess,
  }
}

function resetDevelopmentClientView(): IResetDevelopmentClientViewAction {
  return { type: ActionTypes.ResetDevelopmentClientView }
}

function setAttachments(value: List<IAttachment>): ISetAttachmentsAction {
  return {
    type: ActionTypes.SetAttachments,
    value,
  }
}

function setScope(scope: DevelopmentClientViewScope): ISetScopeAction {
  return {
    scope,
    type: ActionTypes.SetScope,
  }
}

function toggleConfirmDelete(show: boolean): IToggleConfirmDeleteAction {
  return {
    show,
    type: ActionTypes.ToggleConfirmDelete,
  }
}

export const actionCreators = {
  deleteDevelopmentClient,
  loadDevelopmentClient,
  resetDevelopmentClientView,
  setAttachments,
  setScope,
  toggleConfirmDelete,
}

// -------------------------
// REDUCER
// -------------------------

export const reducer: Reducer<DevelopmentClientViewState> = (
  state: DevelopmentClientViewState = new DevelopmentClientViewState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case ActionTypes.DeleteDevelopmentClientFailure:
      return state
        .set('error', action.error)
        .set('isDeleting', false) as DevelopmentClientViewState

    case ActionTypes.DeleteDevelopmentClientRequest:
      return state.set('isDeleting', true) as DevelopmentClientViewState

    case ActionTypes.DeleteDevelopmentClientSuccess:
      return state
        .set('isDeleting', false)
        .set('item', new DevelopmentClient()) as DevelopmentClientViewState

    case ActionTypes.LoadDevelopmentClientFailure:
      return state
        .set('isLoading', false)
        .set('error', action.error) as DevelopmentClientViewState

    case ActionTypes.LoadDevelopmentClientRequest:
      return state.set('isLoading', true) as DevelopmentClientViewState

    case ActionTypes.LoadDevelopmentClientSuccess:
      return state
        .set('isLoading', false)
        .set('item', action.item)
        .set('permissions', action.permissions) as DevelopmentClientViewState

    case ActionTypes.ResetDevelopmentClientView:
      return state.set('isLoading', false) as DevelopmentClientViewState

    case ActionTypes.SetAttachments:
      return state.setIn(
        ['item', 'attachments'],
        action.value,
      ) as DevelopmentClientViewState

    case ActionTypes.SetScope:
      return state.set('scope', action.scope) as DevelopmentClientViewState

    case ActionTypes.ToggleConfirmDelete:
      return state.set(
        'showConfirmDelete',
        action.show,
      ) as DevelopmentClientViewState

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* deleteDevelopmentClientSaga(action: IDeleteDevelopmentClientAction) {
  try {
    yield put(deleteDevelopmentClientRequest())

    const result = yield makeAuthenticatedRequest(
      `/api/DevelopmentClients/${action.id}`,
      { method: RequestMethod.DELETE },
    )

    if (result.success === true) {
      yield put(push('/development/clients'))
      yield put(deleteDevelopmentClientSuccess())
    } else {
      yield put(deleteDevelopmentClientFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(deleteDevelopmentClientFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* loadDevelopmentClientSaga(action: ILoadDevelopmentClientViewAction) {
  try {
    yield put(loadDevelopmentClientRequest())

    const baseUrl = `/api/DevelopmentClients/${action.slug}`

    const result = yield makeAuthenticatedRequest(baseUrl)

    if (result.success === true) {
      const entity = result.entity as DevelopmentClient

      yield put(
        loadDevelopmentClientSuccess(
          DevelopmentClient.fromJS(entity),
          EntityPermissions.fromJS(result.permissions),
        ),
      )
    } else {
      yield put(loadDevelopmentClientFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchDeleteDevelopmentClient() {
  yield takeLatest(
    ActionTypes.DeleteDevelopmentClient,
    deleteDevelopmentClientSaga,
  )
}

function* watchLoadDevelopmentClient() {
  yield takeLatest(ActionTypes.LoadDevelopmentClient, loadDevelopmentClientSaga)
}

export function* clientDetailRootSaga() {
  yield fork(watchDeleteDevelopmentClient)
  yield fork(watchLoadDevelopmentClient)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState = () => (
  state: ApplicationState,
): DevelopmentClientViewState => state.clientDetail

export const selectError = () =>
  createSelector(selectModuleState(), (mState) => mState.error)

export const selectIsDeleting = () =>
  createSelector(selectModuleState(), (mState) => mState.isDeleting)

export const selectIsLoading = () =>
  createSelector(selectModuleState(), (mState) => mState.isLoading)

export const selectItem = () =>
  createSelector(selectModuleState(), (mState) => mState.item)

export const selectPermissions = () =>
  createSelector(selectModuleState(), (mState) => mState.permissions)

export const selectScope = () =>
  createSelector(selectModuleState(), (mState) => mState.scope)

export const selectShowConfirmDelete = () =>
  createSelector(selectModuleState(), (mState) => mState.showConfirmDelete)
